import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import moment from "moment";
import { getCampaignHubs, getCampaignHubDevices } from "../../commonApi";
import { extractErrorMessage } from "../../Utils/ExtractMessages";
import { Loader } from "../../Loader";
import { CAMPAIGN_STATUS_COLOR_CLASS } from "./campaignStatusColors";
import "../../assets/css/CampaignDetailDashboard.css";

const HUB_PAGE_SIZE = 10;
const DEVICE_PAGE_SIZE = 100;

const DEVICE_STATE_LEGEND = [
    { value: "PENDING", label: "Pending", stateClass: "device-state-pending" },
    { value: "DOWNLOADING", label: "Downloading", stateClass: "device-state-downloading" },
    { value: "INSTALLING", label: "Installing", stateClass: "device-state-installing" },
    { value: "RESTARTING", label: "Restarting", stateClass: "device-state-restarting" },
    { value: "RESTARTED", label: "Restarted", stateClass: "device-state-restarting" },
    { value: "SUCCESS", label: "Success", stateClass: "device-state-success" },
    { value: "INSTALLED", label: "Installed", stateClass: "device-state-installed" },
    { value: "SKIPPED", label: "Skipped", stateClass: "device-state-skipped" },
    { value: "FAILED", label: "Failed", stateClass: "device-state-failed" },
];

const DEVICE_STATE_LEGEND_DISPLAY = DEVICE_STATE_LEGEND.filter(
    (item, index, all) => all.findIndex((other) => other.stateClass === item.stateClass) === index
);

const DEVICE_STATE_META = DEVICE_STATE_LEGEND.reduce((acc, item) => {
    acc[item.value] = item;
    return acc;
}, {});

const getStatusBadgeClass = (status) => CAMPAIGN_STATUS_COLOR_CLASS[status] || "bg-light text-dark";

const getHubStateBadgeClass = (state) => {
    switch (state) {
        case "COMPLETED": return "bg-success";
        case "IN_PROGRESS": return "bg-primary";
        case "FAILED": return "bg-danger";
        case "PICKED": return "bg-secondary";
        case "PENDING": return "bg-secondary";
        case "NOTIFIED": return "bg-info";
        default: return "bg-light text-dark";
    }
};

const StatTile = ({ label, value, variant }) => (
    <div className="stat-tile">
        <div className="stat-tile-label">{label}</div>
        <div className={`stat-tile-value stat-tile-${variant || "default"}`}>{value}</div>
    </div>
);

const formatLastUpdated = (timestamp) => (timestamp ? moment(timestamp).fromNow() : "-");

const hubDeviceCache = new Map();

const HubDeviceRow = ({ data, campaignId }) => {
    const cacheKey = `${campaignId}:${data.hubId}`;
    const [state, setState] = useState(() => {
        const cached = hubDeviceCache.get(cacheKey);
        return cached
            ? { loading: false, error: false, devices: cached.devices, totalElements: cached.totalElements }
            : { loading: true, error: false, devices: [], totalElements: 0 };
    });

    useEffect(() => {
        if (hubDeviceCache.has(cacheKey)) return;
        let cancelled = false;

        getCampaignHubDevices(campaignId, data.hubId, { page: 0, size: DEVICE_PAGE_SIZE })
            .then((response) => {
                const page = response?.data?.data;
                const devices = page?.content || [];
                const totalElements = page?.totalElements || 0;
                hubDeviceCache.set(cacheKey, { devices, totalElements });
                if (!cancelled) setState({ loading: false, error: false, devices, totalElements });
            })
            .catch((error) => {
                console.error(`Failed to load devices for hub ${data.hubId}`, error);
                toast.error(extractErrorMessage(error));
                if (!cancelled) setState({ loading: false, error: true, devices: [], totalElements: 0 });
            });

        return () => {
            cancelled = true;
        };
    }, [cacheKey]);

    if (state.loading) {
        return (
            <div className="hub-device-chips hub-device-chips-loading">
                <Loader width="20" />
            </div>
        );
    }

    if (state.error) {
        return (
            <div className="hub-device-chips">
                <span className="hub-device-chips-empty">Unable to load devices for this hub.</span>
            </div>
        );
    }

    if (!state.devices.length) {
        return (
            <div className="hub-device-chips">
                <span className="hub-device-chips-empty">No devices found for this hub.</span>
            </div>
        );
    }

    return (
        <div className="hub-device-chips">
            {state.devices.map((device) => {
                const meta = DEVICE_STATE_META[device.deviceState];
                return (
                    <span key={device.deviceId} className="device-chip" title={device.lastError || undefined}>
                        <span className={`device-chip-dot ${meta?.stateClass || ""}`} />
                        {device.deviceName || device.thingName || device.deviceId}{" "}<strong>{meta?.label || device.deviceState}</strong>
                    </span>
                );
            })}
            {state.totalElements > state.devices.length && (
                <span className="hub-device-chips-empty">
                    Showing {state.devices.length} of {state.totalElements}
                </span>
            )}
        </div>
    );
};

const hubColumns = [
    {
        name: "HUB",
        selector: (row) => row.hubDeviceName || row.hubId,
        sortable: false,
    },
    {
        name: "STATE",
        selector: (row) => row.hubState,
        sortable: false,
        cell: (row) => (
            <span className={`badge ${getHubStateBadgeClass(row.hubState)}`} data-tag="allowRowEvents">
                {row.hubState}
            </span>
        ),
    },
    {
        name: "DEVICES DONE/TOTAL",
        selector: (row) => `${row.devicesDone}/${row.devicesTotal}`,
        sortable: false,
    },
    {
        name: "LAST UPDATED",
        selector: (row) => formatLastUpdated(row.lastUpdated),
        sortable: false,
    },
];

function CampaignDetailDashboard({ show, onClose, loading, data }) {
    const totalHubs = data?.totalHubs ?? 0;
    const totalDevices = data?.totalDevices ?? 0;
    const completedDevices = data?.completedDevices ?? 0;
    const failedDevices = data?.failedDevices ?? 0;
    const skippedDevices = data?.skippedDevices ?? 0;
    const inProgressDevices = data?.inProgressDevices ?? 0;

    const campaignId = data?.campaignId;

    const [hubRows, setHubRows] = useState([]);
    const [hubTotalRows, setHubTotalRows] = useState(0);
    const [hubPage, setHubPage] = useState(0);
    const [hubPageSize, setHubPageSize] = useState(HUB_PAGE_SIZE);
    const [hubLoading, setHubLoading] = useState(false);

    useEffect(() => {
        if (!show) return;
        setHubPage(0);
        hubDeviceCache.clear();
    }, [show, campaignId]);

    useEffect(() => {
        if (!show || !campaignId) return;
        let cancelled = false;
        setHubLoading(true);

        getCampaignHubs(campaignId, { page: hubPage, size: hubPageSize })
            .then((response) => {
                if (cancelled) return;
                const page = response?.data?.data;
                setHubRows(page?.content || []);
                setHubTotalRows(page?.totalElements || 0);
            })
            .catch((error) => {
                if (cancelled) return;
                toast.error(extractErrorMessage(error));
                setHubRows([]);
                setHubTotalRows(0);
            })
            .finally(() => {
                if (!cancelled) setHubLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [show, campaignId, hubPage, hubPageSize]);

    const handleHubPageChange = (page) => {
        setHubPage(page - 1);
    };

    const handleHubPageSizeChange = (newPageSize) => {
        setHubPageSize(newPageSize);
        setHubPage(0);
    };

    return (
        <Modal show={show} onHide={onClose} size="xl" centered dialogClassName="campaign-dashboard-dialog">
            <Modal.Header closeButton>
                <Modal.Title className="campaign-dashboard-title">
                    {data?.name || data?.jobId || "-"}
                    {data?.status && (
                        <span className={`badge ${getStatusBadgeClass(data.status)}`}>{data.status}</span>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="campaign-dashboard-body">
                {loading ? (
                    <div className="campaign-dashboard-loading">
                        <Loader />
                    </div>
                ) : !data ? (
                    <p>Unable to load campaign details.</p>
                ) : (
                    <>
                        <div className="stat-tile-row">
                            <StatTile label="HUBS" value={totalHubs} />
                            <StatTile label="DEVICES" value={totalDevices} />
                            <StatTile label="COMPLETED" value={completedDevices} variant="success" />
                            <StatTile label="FAILED" value={failedDevices} variant="danger" />
                            <StatTile label="SKIPPED" value={skippedDevices} variant="muted" />
                            <StatTile label="IN PROGRESS" value={inProgressDevices} variant="primary" />
                        </div>

                        <div className="device-state-legend">
                            {DEVICE_STATE_LEGEND_DISPLAY.map((item) => (
                                <span key={item.stateClass} className="legend-item">
                                    <span className={`legend-dot ${item.stateClass}`} />
                                    {item.label}
                                </span>
                            ))}
                        </div>

                        <div className="hub-table-wrap">
                            <DataTable
                                className="hub-datatable-scroll"
                                columns={hubColumns}
                                data={hubRows}
                                progressPending={hubLoading}
                                progressComponent={(
                                    <div className="hub-table-loading">
                                        <Loader />
                                    </div>
                                )}
                                expandableRows
                                expandableRowsComponent={(props) => (
                                    <HubDeviceRow
                                        {...props}
                                        campaignId={campaignId}
                                    />
                                )}
                                expandOnRowClicked
                                highlightOnHover
                                dense
                                fixedHeader
                                pagination
                                paginationServer
                                paginationTotalRows={hubTotalRows}
                                paginationDefaultPage={hubPage + 1}
                                paginationPerPage={hubPageSize}
                                paginationRowsPerPageOptions={[10, 20, 50]}
                                onChangePage={handleHubPageChange}
                                onChangeRowsPerPage={handleHubPageSizeChange}
                            />
                        </div>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}

export default CampaignDetailDashboard;
